import React, { useEffect, useMemo, useState } from 'react'
import { Select, Drawer, Input, Empty } from 'antd'
import { CheckOutlined, DownOutlined, CloseCircleFilled } from '@ant-design/icons'
import styled from 'styled-components'

/**
 * 手機／平板改用底部彈出清單的 Select
 *
 * 為什麼要做這個：
 *   antd 的下拉選單是為滑鼠設計的 —— 它把選項渲染在一個高度固定的浮層裡，
 *   用手指滑動時，清單捲到底之後動量會外溢給整頁（scroll chaining），
 *   浮層本身又會跟著頁面捲動而重新定位。結果就是「想選東西卻在滑頁面」。
 *   加 overscroll-behavior 只能減輕，治不了根本 —— 浮層在觸控裝置上
 *   本來就不是對的互動形式。
 *
 *   所以在 <=1024px（含 iPad 橫向）直接換成底部抽屜：
 *   佔滿寬度、有自己的捲動區、選項是 48px 的觸控目標，
 *   選完就關。桌機維持原本的 antd Select，完全不變。
 *
 * 用法與 antd Select 相容（單選）：
 *   - options={[{value,label}]} 或 <Option> 子元素，兩種都吃
 *   - onChange(value, option) 兩個參數都會給（有呼叫端會讀 option 上的自訂欄位）
 *   - value / placeholder / disabled / allowClear / showSearch / size / style
 *   - 其餘 props（getPopupContainer、dropdownStyle…）只在桌機透傳
 *
 * 不支援 mode='multiple'／'tags' —— 目前沒有用到；
 * 真的要用時會退回桌機版 Select，不會壞掉。
 */

const Trigger = styled.button`
  width: 100%;
  min-height: ${({ $size }) => ($size === 'large' ? '48px' : '40px')};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 11px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 16px; /* iOS 聚焦不放大的門檻 */
  color: ${({ $placeholder }) => ($placeholder ? '#bfbfbf' : 'rgba(0,0,0,0.88)')};
  text-align: left;
  cursor: pointer;

  &:disabled {
    background: #f5f5f5;
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }

  .label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .icon {
    flex: none;
    color: rgba(0, 0, 0, 0.25);
    font-size: 12px;
  }
`

const OptionList = styled.div`
  max-height: 55vh;
  overflow-y: auto;
  /* 清單捲到底不要把動量交給整頁 —— 這正是原本難滑的主因 */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  margin: 0 -16px;
`

/* 分組標題（options 用 { label, options } 分組時顯示） */
const GroupLabel = styled.div`
  padding: 10px 16px 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #8c8c8c;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 1;
`

const OptionRow = styled.button`
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ $active }) => ($active ? '#fdf0f3' : 'transparent')};
  border: none;
  border-bottom: 1px solid #f5f5f5;
  font-size: 15.5px;
  line-height: 1.5;
  color: ${({ $active }) => ($active ? '#c9637d' : 'rgba(0,0,0,0.88)')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: left;
  cursor: pointer;

  &:active:not(:disabled) {
    background: #f7f7f9;
  }

  &:disabled {
    color: rgba(0, 0, 0, 0.25);
    cursor: not-allowed;
  }

  .txt {
    flex: 1;
    min-width: 0;
  }
`

const ClearRow = styled(OptionRow)`
  color: rgba(0, 0, 0, 0.45);
  font-weight: 400;
`

/**
 * 把任意 ReactNode 抽成可顯示的文字。
 * Option 的內容常常是 {name} 或 `${name} - $${price}` 這種組合，
 * 直接拿來當 label 會是陣列或物件，所以要攤平成字串。
 */
const nodeToText = (node) => {
  if (node == null || node === false) return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeToText).join('')
  if (node.props?.children != null) return nodeToText(node.props.children)
  return ''
}

/**
 * 從 options prop 或 <Option> 子元素統一整理出 {value,label,group,raw} 陣列
 *
 * 必須把「分組選項」攤平：antd 允許 options 寫成
 *   [{ label: '分類名', options: [{ value, label }, ...] }, ...]
 * 若照單全收，攤出來的每一筆 value 都是 undefined，會造成
 *   · selected 永遠找不到 → 明明有值卻顯示 placeholder
 *   · 清單列出來的是「分類名」，點下去等於選了 undefined（看起來像沒反應）
 * 這也是結帳頁「服務項目」在手機／平板點不到的原因。
 */
const normalizeOptions = (options, children) => {
  const out = []

  if (Array.isArray(options)) {
    options.forEach((o) => {
      if (!o) return
      // 分組：{ label: '分類名', options: [...] }
      if (Array.isArray(o.options)) {
        const groupLabel =
          typeof o.label === 'string' ? o.label : nodeToText(o.label)
        o.options.forEach((child) => {
          if (!child || child.value === undefined) return
          out.push({
            value: child.value,
            label:
              typeof child.label === 'string'
                ? child.label
                : nodeToText(child.label),
            disabled: child.disabled,
            group: groupLabel,
            raw: child
          })
        })
        return
      }
      out.push({
        value: o.value,
        label: typeof o.label === 'string' ? o.label : nodeToText(o.label),
        disabled: o.disabled,
        raw: o
      })
    })
    return out
  }

  const walk = (nodes, group) => {
    React.Children.forEach(nodes, (child) => {
      if (!child || typeof child !== 'object') return
      // <>...</> 包住一批 Option 是很常見的寫法，
      // React.Children.forEach 不會自動展開，得自己遞迴下去，
      // 否則整批選項會安靜地消失
      if (child.type === React.Fragment) {
        walk(child.props?.children, group)
        return
      }
      if (!child.props) return
      // <Select.OptGroup label='分類'> 同樣要往下走，否則整組選項會消失
      if (child.props.value === undefined && child.props.children != null) {
        walk(
          child.props.children,
          nodeToText(child.props.label) || group
        )
        return
      }
      if (child.props.value === undefined) return
      const { value, children: label } = child.props
      out.push({
        value,
        label: nodeToText(label) || String(value ?? ''),
        disabled: child.props.disabled,
        group,
        raw: { ...child.props }
      })
    })
  }
  walk(children, undefined)
  return out
}

const ResponsiveSelect = ({
  value,
  onChange,
  options,
  children,
  placeholder = '請選擇',
  disabled = false,
  allowClear = false,
  showSearch = false,
  size,
  style,
  className,
  mode,
  drawerTitle,
  ...rest
}) => {
  const [isTouchLayout, setIsTouchLayout] = useState(false)
  const [open, setOpen] = useState(false)
  const [keyword, setKeyword] = useState('')

  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => setIsTouchLayout(window.innerWidth <= 1024)
    sync()
    window.addEventListener('resize', sync)
    window.addEventListener('orientationchange', sync)
    return () => {
      window.removeEventListener('resize', sync)
      window.removeEventListener('orientationchange', sync)
    }
  }, [])

  const list = useMemo(
    () => normalizeOptions(options, children),
    [options, children]
  )

  const selected = list.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const k = keyword.trim().toLowerCase()
    if (!k) return list
    return list.filter((o) => String(o.label).toLowerCase().includes(k))
  }, [list, keyword])

  // 桌機、或用到尚未支援的多選模式時，原封不動交給 antd
  if (!isTouchLayout || mode) {
    return (
      <Select
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        showSearch={showSearch}
        size={size}
        style={style}
        className={className}
        mode={mode}
        {...rest}
      >
        {children}
      </Select>
    )
  }

  const pick = (opt) => {
    setOpen(false)
    setKeyword('')
    onChange?.(opt?.value, opt?.raw)
  }

  return (
    <>
      <Trigger
        type='button'
        className={className}
        style={style}
        $size={size}
        $placeholder={!selected}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <span className='label'>{selected ? selected.label : placeholder}</span>
        {allowClear && selected && (
          <CloseCircleFilled
            className='icon'
            onClick={(e) => {
              // 不要順帶把抽屜打開
              e.stopPropagation()
              onChange?.(undefined, undefined)
            }}
          />
        )}
        <DownOutlined className='icon' />
      </Trigger>

      <Drawer
        placement='bottom'
        open={open}
        onClose={() => {
          setOpen(false)
          setKeyword('')
        }}
        title={drawerTitle || placeholder}
        height='auto'
        styles={{
          body: { padding: '0 16px 16px' },
          content: { borderRadius: '16px 16px 0 0' }
        }}
      >
        {showSearch && (
          <Input
            allowClear
            placeholder='搜尋'
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            style={{ marginBottom: 12, height: 44, fontSize: 16 }}
          />
        )}

        <OptionList>
          {allowClear && selected && (
            <ClearRow type='button' onClick={() => pick(undefined)}>
              <span className='txt'>清除選擇</span>
            </ClearRow>
          )}
          {filtered.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='沒有符合的項目'
              style={{ padding: '24px 0' }}
            />
          ) : (
            filtered.map((o, idx) => {
              // 同一組只在第一筆前面顯示一次標題
              const showGroup =
                !!o.group && (idx === 0 || filtered[idx - 1].group !== o.group)
              return (
                <React.Fragment key={String(o.value)}>
                  {showGroup && <GroupLabel>{o.group}</GroupLabel>}
                  <OptionRow
                    type='button'
                    disabled={o.disabled}
                    $active={o.value === value}
                    onClick={() => pick(o)}
                  >
                    <span className='txt'>{o.label}</span>
                    {o.value === value && <CheckOutlined />}
                  </OptionRow>
                </React.Fragment>
              )
            })
          )}
        </OptionList>
      </Drawer>
    </>
  )
}

export default ResponsiveSelect
